import { faker } from '@faker-js/faker';
import { formatPhoneNumber } from './phoneFormatter';
import { generatePatientData } from './dataGenerators';
import { generateConstraints, generateAppointments } from './constraintBuilders';
import { buildCaseSpecificData } from './caseDataBuilders';

/**
 * Hawkeye outcomePlanType values that use the Hawkeye request format.
 */
export const HAWKEYE_PLAN_TYPES = [
  'PATIENT_FOLLOW_UP_APPOINTMENT_CONFIRMATION',
  'PATIENT_FOLLOW_UP_APPOINTMENT_RESCHEDULING',
  'PATIENT_FOLLOW_UP_DATA_COLLECTION',
];

export const isHawkeyePlanType = (planType) => HAWKEYE_PLAN_TYPES.includes(planType);

/**
 * Builds the standard request object for case creation
 * @param {object} options - Build options
 * @returns {object} Request object for API
 */
export const buildStandardRequestObject = ({
  formData,
  planId,
  planType,
  aptType,
  cdtCodes,
  providerId,
}) => {
  const fakeData = generatePatientData();
  const isMinimalOrCustom = formData.ctype === 'Custom - All fields' || formData.ctype === 'Minimal - Select fields';
  const formattedPhoneNumber = formatPhoneNumber(formData.phoneNumber);

  const constraints = generateConstraints();
  const appointments = generateAppointments({
    aptType,
    planName: formData.plan,
    providerId,
  });

  const firstName = isMinimalOrCustom ? formData.firstName : fakeData.firstName;
  const lastName = isMinimalOrCustom ? formData.lastName : fakeData.lastName;

  return {
    name: formData.plan + " Test",
    outcomePlanId: {
      id: planId,
      versionNumber: 1
    },
    cdtCodes: cdtCodes || [],
    providerId,
    patient: {
      firstName,
      lastName,
      dob: isMinimalOrCustom ? formData.dob : fakeData.dob,
      gender: isMinimalOrCustom ? formData.gender : fakeData.gender,
      phoneNumber: formattedPhoneNumber,
      postalCode: isMinimalOrCustom ? formData.postalCode : fakeData.postalCode,
      mobilePhoneNumber: formattedPhoneNumber,
      mrn: fakeData.mrn,
      umrn: fakeData.umrn,
      source: process.env.REACT_APP_FORM_SOURCE,
      pronoun: fakeData.pronoun,
      conversationStyle: fakeData.conversationStyle,
      communicationChannel: formData.channel,
      constraintList: constraints,
      ...(formData.patientExternalId && { externalId: formData.patientExternalId }),
    },
    state: "ACTIVE",
    communicationChannel: formData.channel,
    searchText: formData.plan + ' ' + firstName + ' ' + lastName,
    appointments,
    medications: []
  };
};

/**
 * Builds request object for plans with custom caseSpecificData fields
 * @param {object} options - Build options
 * @param {object} options.formData - Form data
 * @param {string} options.planId - Plan ID
 * @param {string} options.providerId - Provider ID
 * @param {object} options.customFieldValues - Custom field values
 * @param {object} [options.orgContext={}] - Organization context for config lookup
 * @returns {object} Request object for API
 */
export const buildCustomFieldsRequestObject = ({
  formData,
  planId,
  providerId,
  customFieldValues,
  orgContext = {},
  planType = null,
}) => {
  const fakeData = generatePatientData();
  const isMinimal = formData.ctype === 'Minimal - Select fields';
  const formattedPhoneNumber = formatPhoneNumber(formData.phoneNumber);

  return {
    name: formData.plan + " Test",
    outcomePlanId: {
      id: planId,
      versionNumber: 1
    },
    patient: {
      patientId: fakeData.patientId,
      firstName: isMinimal ? formData.firstName : fakeData.firstName,
      lastName: isMinimal ? formData.lastName : fakeData.lastName,
      dob: isMinimal ? formData.dob : fakeData.dob,
      gender: isMinimal ? formData.gender : fakeData.gender,
      pronoun: fakeData.pronoun,
      mobilePhoneNumber: formattedPhoneNumber,
      postalCode: fakeData.postalCode,
      externalId: formData.patientExternalId || fakeData.externalId,
    },
    state: "ACTIVE",
    communicationChannel: formData.channel,
    providerId,
    appointments: [
      {
        title: "Annual",
        type: "PCP_FOLLOW_UP",
        date: null,
        providerId: null
      }
    ],
    preferredLanguage: "en-US",
    caseSpecificData: buildCaseSpecificData(formData.plan, customFieldValues, orgContext, planType, formData),
    assistantGeneratedData: {}
  };
};

/**
 * Formats a 10-digit phone number to E.164 format: +1XXXXXXXXXX
 * @param {string} phoneNumber - 10 digit phone number
 * @returns {string} E.164 formatted phone number
 */
const formatPhoneE164 = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  const digits = phoneNumber.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
};

/**
 * Normalizes a datetime-local value to ISO 8601 with Z suffix.
 * @param {string} value - datetime-local input value
 * @returns {string} ISO 8601 formatted string
 */
const normalizeDateTimeValue = (value) => {
  if (!value) return value;
  if (value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)) return value;
  const parts = value.split('T');
  if (parts.length === 2 && parts[1].split(':').length === 2) {
    return value + ':00Z';
  }
  return value + 'Z';
};

/**
 * Builds the appointments array for Hawkeye missing info plans.
 * Structure: externalId, name, type (no date, no providerData).
 */
const buildMissingInfoAppointments = ({ aptTypes, customFieldValues }) => {
  if (!aptTypes || aptTypes.length === 0) return null;

  return aptTypes.map((type) => ({
    externalId: customFieldValues.externalAppointmentId || '',
    name: customFieldValues.appointmentName || 'Missing Information Collection',
    type,
  }));
};

/**
 * Builds the appointments array for Hawkeye confirmation/rescheduling plans.
 * Structure: title, type, date, externalId, externalTypeId, providerData.
 */
const buildConfirmationReschedulingAppointments = ({ aptTypes, customFieldValues }) => {
  if (!aptTypes || aptTypes.length === 0) return null;

  return aptTypes.map((type) => ({
    title: customFieldValues.appointmentType || type,
    type,
    date: normalizeDateTimeValue(customFieldValues.startTime) || null,
    externalId: customFieldValues.appointmentId || faker.string.uuid(),
    providerData: {
      title: '',
      providerName: customFieldValues.providerName || '',
    },
  }));
};

/**
 * Builds request object for Hawkeye tenant plans.
 * Source: "THIRD_PARTY", organizationGroupId/organizationId on patient.
 * Appointments are built from caseSpecificData fields when the plan requires them,
 * otherwise set to null.
 *
 * @param {object} options - Build options
 * @param {object} options.formData - Form data
 * @param {string} options.planId - Plan ID
 * @param {string} options.providerId - Provider ID
 * @param {object} options.customFieldValues - Custom field values
 * @param {object} [options.orgContext={}] - Organization context
 * @param {string} [options.planType=null] - outcomePlanType
 * @param {string[]} [options.aptTypes=[]] - Appointment types from plan details
 * @returns {object} Request object for API
 */
export const buildHawkeyeRequestObject = ({
  formData,
  planId,
  providerId,
  customFieldValues,
  orgContext = {},
  planType = null,
  aptTypes = [],
}) => {
  const fakeData = generatePatientData();
  const isMinimal = formData.ctype === 'Minimal - Select fields';
  const phoneE164 = formatPhoneE164(formData.phoneNumber);

  const firstName = isMinimal ? formData.firstName : fakeData.firstName;
  const lastName = isMinimal ? formData.lastName : fakeData.lastName;

  const isMissingInfo = planType === 'PATIENT_FOLLOW_UP_DATA_COLLECTION';

  const appointments = isMissingInfo
    ? buildMissingInfoAppointments({ aptTypes, customFieldValues })
    : buildConfirmationReschedulingAppointments({ aptTypes, customFieldValues });

  const result = {
    patient: {
      firstName,
      lastName,
      dob: isMinimal ? formData.dob : fakeData.dob,
      phoneNumber: phoneE164,
      mobilePhoneNumber: phoneE164,
      externalId: formData.patientExternalId || fakeData.externalId,
      source: 'THIRD_PARTY',
    },
    name: formData.plan,
    outcomePlanId: {
      id: planId,
      versionNumber: 1,
    },
    providerId,
    state: 'ACTIVE',
    preferredLanguage: 'en-US',
    communicationChannel: formData.channel,
    appointments,
  };

  if (isMissingInfo) {
    const rawMissing = customFieldValues.missingData;
    const missingData = Array.isArray(rawMissing)
      ? rawMissing.map((s) => String(s).trim()).filter(Boolean)
      : String(rawMissing || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

    result.caseSpecificData = {
      externalAppointmentId: customFieldValues.externalAppointmentId || '',
      missingData,
    };
  }

  return result;
};

/**
 * Adjusts appointment date for specific plans
 * @param {object} requestObject - The request object to modify
 * @param {string} planName - Name of the plan
 * @returns {object} Modified request object
 */
export const adjustAppointmentDateForPlan = (requestObject, planName) => {
  if (planName === 'MIMIT Appointment Reminder' || planName === 'Outreach-Appointment-Rescheduling_RW') {
    const adate = new Date();
    adate.setDate(adate.getDate() + 10);
    requestObject.appointments[0].date = adate.toISOString();
  }
  return requestObject;
};
